import React, { useState } from 'react';
import API from 'shared/api';
import {
  FiX,
  FiChevronRight,
  FiChevronLeft,
  FiEye,
  FiEyeOff,
  FiDownload,
  FiCheck,
  FiShield,
  FiAlertTriangle,
  FiFolder,
} from 'react-icons/fi';
import { TIPOS_BD, SS } from './syncUtils';

const FRECUENCIAS = [
  { v: 15, l: 'Cada 15 minutos' },
  { v: 30, l: 'Cada 30 minutos' },
  { v: 60, l: 'Cada hora' },
  { v: 120, l: 'Cada 2 horas' },
  { v: 240, l: 'Cada 4 horas' },
  { v: 480, l: 'Cada 8 horas' },
];

const PASOS = ['Sede', 'Base de datos', 'Conexion', 'Instalar'];

/* Descarga del instalador (.exe). Reutilizable: wizard final y "Regenerar acceso". */
export function InstaladorDescarga({ sede, codigo, instaladorDisponible, onFinish }) {
  const [bajando, setBajando] = useState(false);
  const [bajado, setBajado] = useState(false);
  const [err, setErr] = useState(null);

  const descargar = async () => {
    setBajando(true);
    setErr(null);
    try {
      const res = await API.get(
        `/sync/descargar-instalador?sede_codigo=${encodeURIComponent(sede)}&codigo=${encodeURIComponent(codigo)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AgoraxSync_Setup_${codigo}.exe`;
      a.click();
      URL.revokeObjectURL(url);
      setBajado(true);
    } catch (e) {
      if (e.response?.status === 503)
        setErr('El instalador estara disponible muy pronto. Intenta de nuevo en unos minutos.');
      else setErr('No se pudo descargar el instalador. Intenta de nuevo.');
    } finally {
      setBajando(false);
    }
  };

  const pasos = [
    {
      n: 1,
      t: 'Descarga el instalador',
      d: 'Se guardara en la carpeta de Descargas de este equipo.',
    },
    {
      n: 2,
      t: 'Ejecutalo (doble clic)',
      d: 'Solo una vez. Se conectará a tu base de datos y hará la primera sincronización.',
    },
    {
      n: 3,
      t: 'Listo',
      d: 'El programa queda funcionando solo. Veras el estado aqui mismo, en vivo.',
    },
  ];

  return (
    <div className="sync-anim">
      <div style={{ textAlign: 'center', marginBottom: '18px' }}>
        <div
          style={{
            width: '54px',
            height: '54px',
            borderRadius: '14px',
            background: '#ecfdf5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '10px',
          }}
        >
          <FiDownload size={24} color="#059669" />
        </div>
        <div style={{ fontSize: '17px', fontWeight: 700, color: '#111827' }}>
          Instala el sincronizador
        </div>
        <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
          Debes ejecutarlo en el equipo donde está tu base de datos.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
        {pasos.map((p) => (
          <div
            key={p.n}
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              padding: '12px 14px',
              background: '#f8f9fb',
              borderRadius: '12px',
              border: '1px solid #eef0f4',
            }}
          >
            <div
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                background: '#111827',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {p.n}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{p.t}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{p.d}</div>
            </div>
          </div>
        ))}
      </div>

      {!instaladorDisponible && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
            padding: '11px 14px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '10px',
            marginBottom: '14px',
          }}
        >
          <FiAlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '12px', color: '#92400e' }}>
            El instalador aún se está preparando en el servidor. La configuración ya quedó guardada;
            podrás descargarlo en breve.
          </div>
        </div>
      )}

      {err && (
        <div
          style={{
            fontSize: '12px',
            color: '#dc2626',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '10px',
            padding: '10px 14px',
            marginBottom: '14px',
          }}
        >
          {err}
        </div>
      )}

      <button
        onClick={descargar}
        disabled={bajando}
        style={{ ...SS.btnAccent, width: '100%', opacity: bajando ? 0.6 : 1, padding: '13px' }}
      >
        {bajando ? (
          'Descargando...'
        ) : bajado ? (
          <>
            <FiCheck size={16} /> Descargar de nuevo
          </>
        ) : (
          <>
            <FiDownload size={16} /> Descargar instalador
          </>
        )}
      </button>

      {bajado && (
        <div
          style={{
            fontSize: '12px',
            color: '#059669',
            textAlign: 'center',
            marginTop: '10px',
            fontWeight: 500,
          }}
        >
          Descargado. Ejecuta el archivo en el equipo del servidor para terminar.
        </div>
      )}

      <button onClick={onFinish} style={{ ...SS.btnOutline, width: '100%', marginTop: '12px' }}>
        {bajado ? 'Entendido, ver estado' : 'Cerrar'}
      </button>
    </div>
  );
}

/* Wizard de configuracion (crear). 4 pasos. */
export default function SyncWizard({ sedes, sedeInicial, onClose, onConfigurado }) {
  const [paso, setPaso] = useState(1);
  const [form, setForm] = useState({
    sede_codigo: sedeInicial || (sedes[0] && sedes[0].sede_codigo) || '',
    tipo_bd: '',
    host: '',
    puerto: '',
    nombre_bd: '',
    usuario: '',
    password: '',
    frecuencia: 60,
  });
  const [showPass, setShowPass] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [codigo, setCodigo] = useState(null);
  const [instaladorDisp, setInstaladorDisp] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const elegirTipo = (t) => {
    setForm((f) => ({ ...f, tipo_bd: t.v, puerto: f.puerto || String(t.puerto) }));
    setError(null);
    setPaso(3);
  };

  const guardar = async () => {
    setError(null);
    if (!form.host.trim() || !form.nombre_bd.trim() || !form.usuario.trim() || !form.password) {
      setError('Completa todos los campos para continuar.');
      return;
    }
    setGuardando(true);
    try {
      const res = await API.post('/sync/configurar', {
        sede_codigo: form.sede_codigo,
        tipo_bd: form.tipo_bd,
        host: form.host.trim(),
        puerto: parseInt(form.puerto, 10) || undefined,
        nombre_bd: form.nombre_bd.trim(),
        usuario: form.usuario.trim(),
        password: form.password,
        frecuencia_minutos: parseInt(form.frecuencia, 10) || 60,
      });
      if (res.data.status === 'success') {
        setCodigo(res.data.data.codigo_activacion);
        setInstaladorDisp(!!res.data.data.instalador_disponible);
        setPaso(4);
      } else {
        setError(res.data.message || 'No se pudo guardar la configuración.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar la configuracion.');
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = () => {
    if (codigo) onConfigurado();
    else onClose();
  };

  return (
    <div style={SS.overlay} onClick={cerrar}>
      <div
        className="sync-modal"
        style={{
          background: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '520px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + progreso */}
        <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
              Configurar sincronización
            </div>
            <button
              onClick={cerrar}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '4px',
                display: 'flex',
              }}
            >
              <FiX size={20} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
            {PASOS.map((p, i) => (
              <div key={p} style={{ flex: 1 }}>
                <div
                  style={{
                    height: '4px',
                    borderRadius: '999px',
                    background: i + 1 <= paso ? '#2563eb' : '#e5e7eb',
                    transition: 'background 0.3s',
                  }}
                />
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: i + 1 === paso ? '#2563eb' : '#9ca3af',
                    marginTop: '5px',
                    textAlign: 'center',
                  }}
                  className="sync-hide-sm"
                >
                  {p}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="sync-modal-body" style={{ padding: '20px 24px 24px', overflowY: 'auto' }}>
          {/* Paso 1: Sede */}
          {paso === 1 && (
            <div className="sync-anim">
              <div
                style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}
              >
                Para que sede es?
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
                Cada sede se sincroniza con su propia base de datos.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sedes.map((s) => {
                  const sel = form.sede_codigo === s.sede_codigo;
                  return (
                    <button
                      key={s.sede_codigo}
                      onClick={() => set('sede_codigo', s.sede_codigo)}
                      style={{
                        textAlign: 'left',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: sel ? '2px solid #2563eb' : '1.5px solid #e5e7eb',
                        background: sel ? '#eff6ff' : '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                          {s.sede_nombre || s.sede_codigo}
                        </div>
                        {s.configurada && (
                          <div style={{ fontSize: '11px', color: '#d97706', marginTop: '2px' }}>
                            Ya tiene una configuración (se reemplazará)
                          </div>
                        )}
                      </div>
                      {sel && <FiCheck size={18} color="#2563eb" />}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '22px' }}>
                <button
                  onClick={() => setPaso(2)}
                  disabled={!form.sede_codigo}
                  style={{ ...SS.btnPrimary, opacity: form.sede_codigo ? 1 : 0.5 }}
                >
                  Continuar <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Paso 2: Tipo BD */}
          {paso === 2 && (
            <div className="sync-anim">
              <div
                style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}
              >
                Que sistema usas?
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
                Elige el motor de tu base de datos.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {TIPOS_BD.map((t) => (
                  <button
                    key={t.v}
                    onClick={() => elegirTipo(t)}
                    style={{
                      textAlign: 'left',
                      padding: '16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: form.tipo_bd === t.v ? '2px solid #2563eb' : '1.5px solid #e5e7eb',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontSize: '26px' }}>{t.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>
                        {t.label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {t.desc} · puerto {t.puerto}
                      </div>
                    </div>
                    <FiChevronRight size={18} color="#9ca3af" />
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '22px' }}>
                <button onClick={() => setPaso(1)} style={SS.btnOutline}>
                  <FiChevronLeft size={16} /> Atras
                </button>
              </div>
            </div>
          )}

          {/* Paso 3: Datos de conexion */}
          {paso === 3 && (
            <div className="sync-anim">
              <div
                style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}
              >
                Datos de conexión
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
                Los datos con los que tu sistema entra a la base de datos.
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px',
                  gap: '12px',
                  marginBottom: '12px',
                }}
              >
                <div>
                  <label style={SS.label}>Dirección o IP del servidor</label>
                  <input
                    value={form.host}
                    onChange={(e) => set('host', e.target.value)}
                    placeholder="localhost"
                    style={SS.input}
                    autoFocus
                  />
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '3px' }}>
                    Usa localhost si la base de datos está en esta misma computadora.
                  </div>
                </div>
                <div>
                  <label style={SS.label}>Puerto</label>
                  <input
                    value={form.puerto}
                    onChange={(e) => set('puerto', e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="1433"
                    style={SS.input}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={SS.label}>
                  {form.tipo_bd === 'firebird'
                    ? 'Ruta del archivo de base de datos'
                    : 'Nombre de la base de datos'}
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={form.nombre_bd}
                    onChange={(e) => set('nombre_bd', e.target.value)}
                    placeholder={
                      form.tipo_bd === 'firebird' ? 'C:\\datos\\inventario.fdb' : 'mi_inventario'
                    }
                    style={{ ...SS.input, flex: 1 }}
                  />
                  {form.tipo_bd === 'firebird' && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if (window.showOpenFilePicker) {
                            const [fh] = await window.showOpenFilePicker({
                              types: [
                                {
                                  description: 'Firebird DB',
                                  accept: { 'application/octet-stream': ['.fdb', '.gdb'] },
                                },
                              ],
                              startIn: 'desktop',
                            });
                            if (fh) {
                              const file = await fh.getFile();
                              set('nombre_bd', file.name);
                            }
                          } else {
                            alert(
                              'Tu navegador no soporta seleccion de archivos. Escribe la ruta completa manualmente (ej: C:\\datos\\inventario.fdb).'
                            );
                          }
                        } catch (_) {
                          /* usuario cancelo */
                        }
                      }}
                      title="Examinar en busca del archivo .fdb / .gdb"
                      style={{
                        ...SS.btnOutline,
                        padding: '10px 14px',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flexShrink: 0,
                      }}
                    >
                      <FiFolder size={16} /> Examinar
                    </button>
                  )}
                </div>
                {form.tipo_bd === 'firebird' && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    Archivo .fdb o .gdb de Firebird en este equipo.
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  marginBottom: '12px',
                }}
              >
                <div>
                  <label style={SS.label}>Usuario</label>
                  <input
                    value={form.usuario}
                    onChange={(e) => set('usuario', e.target.value)}
                    placeholder="sa"
                    style={SS.input}
                  />
                </div>
                <div>
                  <label style={SS.label}>Contraseña</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => set('password', e.target.value)}
                      placeholder="••••••••"
                      style={{ ...SS.input, paddingRight: '38px' }}
                    />
                    <button
                      onClick={() => setShowPass((v) => !v)}
                      type="button"
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#9ca3af',
                        padding: '4px',
                        display: 'flex',
                      }}
                    >
                      {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={SS.label}>Con que frecuencia sincronizar?</label>
                <select
                  value={form.frecuencia}
                  onChange={(e) => set('frecuencia', parseInt(e.target.value, 10))}
                  style={{ ...SS.input, cursor: 'pointer' }}
                >
                  {FRECUENCIAS.map((f) => (
                    <option key={f.v} value={f.v}>
                      {f.l}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'flex-start',
                  padding: '11px 14px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '10px',
                  marginBottom: '16px',
                }}
              >
                <FiShield size={16} color="#059669" style={{ flexShrink: 0, marginTop: '1px' }} />
                <div style={{ fontSize: '12px', color: '#166534' }}>
                  Tus datos se guardan cifrados y nunca se comparten con terceros. Solo se usan para
                  conectar con tu base de datos.
                </div>
              </div>

              {error && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                <button onClick={() => setPaso(2)} style={SS.btnOutline}>
                  <FiChevronLeft size={16} /> Atras
                </button>
                <button
                  onClick={guardar}
                  disabled={guardando}
                  style={{ ...SS.btnPrimary, opacity: guardando ? 0.6 : 1 }}
                >
                  {guardando ? (
                    'Guardando...'
                  ) : (
                    <>
                      Guardar y continuar <FiChevronRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Paso 4: Instalar */}
          {paso === 4 && codigo && (
            <InstaladorDescarga
              sede={form.sede_codigo}
              codigo={codigo}
              instaladorDisponible={instaladorDisp}
              onFinish={onConfigurado}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* Modal de edicion de parametros (solo remoto: el agente aplica en el proximo latido). */
export function SyncEditModal({ sede, onClose, onGuardado }) {
  const cfg = sede.config || {};
  const [form, setForm] = useState({
    tipo_bd: cfg.tipo_bd || 'sqlserver',
    host: cfg.host || '',
    puerto: cfg.puerto != null ? String(cfg.puerto) : '',
    nombre_bd: cfg.nombre_bd || '',
    usuario: cfg.usuario || '',
    password: '',
    frecuencia: cfg.frecuencia_minutos || 60,
  });
  const [showPass, setShowPass] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async () => {
    setError(null);
    setGuardando(true);
    try {
      const body = {
        sede_codigo: sede.sede_codigo,
        tipo_bd: form.tipo_bd,
        host: form.host.trim(),
        puerto: parseInt(form.puerto, 10) || undefined,
        nombre_bd: form.nombre_bd.trim(),
        usuario: form.usuario.trim(),
        frecuencia_minutos: parseInt(form.frecuencia, 10) || 60,
      };
      if (form.password) body.password = form.password;
      const res = await API.put('/sync/configurar', body);
      if (res.data.status === 'success') onGuardado();
      else setError(res.data.message || 'No se pudo guardar');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar los cambios.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={SS.overlay} onClick={onClose}>
      <div
        className="sync-modal"
        style={{
          background: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
            Editar parametros
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px',
              display: 'flex',
            }}
          >
            <FiX size={20} />
          </button>
        </div>
        <div className="sync-modal-body" style={{ padding: '20px 24px', overflowY: 'auto' }}>
          <div
            style={{
              fontSize: '12px',
              color: '#6b7280',
              background: '#f8f9fb',
              border: '1px solid #eef0f4',
              borderRadius: '10px',
              padding: '10px 14px',
              marginBottom: '16px',
            }}
          >
            Los cambios se aplican de forma automática en la próxima conexión. No necesitas
            reinstalar nada.
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={SS.label}>Sistema</label>
            <select
              value={form.tipo_bd}
              onChange={(e) => set('tipo_bd', e.target.value)}
              style={{ ...SS.input, cursor: 'pointer' }}
            >
              {TIPOS_BD.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 110px',
              gap: '12px',
              marginBottom: '12px',
            }}
          >
            <div>
              <label style={SS.label}>Direccion o IP</label>
              <input
                value={form.host}
                onChange={(e) => set('host', e.target.value)}
                placeholder="localhost"
                style={SS.input}
              />
            </div>
            <div>
              <label style={SS.label}>Puerto</label>
              <input
                value={form.puerto}
                onChange={(e) => set('puerto', e.target.value.replace(/[^0-9]/g, ''))}
                style={SS.input}
              />
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={SS.label}>
              {form.tipo_bd === 'firebird' ? 'Ruta del archivo' : 'Base de datos'}
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                value={form.nombre_bd}
                onChange={(e) => set('nombre_bd', e.target.value)}
                style={{ ...SS.input, flex: 1 }}
              />
              {form.tipo_bd === 'firebird' && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (window.showOpenFilePicker) {
                        const [fh] = await window.showOpenFilePicker({
                          types: [
                            {
                              description: 'Firebird DB',
                              accept: { 'application/octet-stream': ['.fdb', '.gdb'] },
                            },
                          ],
                          startIn: 'desktop',
                        });
                        if (fh) {
                          const f = await fh.getFile();
                          set('nombre_bd', f.name);
                        }
                      } else {
                        alert(
                          'Tu navegador no soporta seleccion de archivos. Escribe la ruta manualmente (ej: C:\\datos\\inventario.fdb).'
                        );
                      }
                    } catch (_) {
                      /* cancelo */
                    }
                  }}
                  title="Examinar"
                  style={{
                    ...SS.btnOutline,
                    padding: '10px 14px',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                  }}
                >
                  <FiFolder size={16} /> Examinar
                </button>
              )}
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '12px',
            }}
          >
            <div>
              <label style={SS.label}>Usuario</label>
              <input
                value={form.usuario}
                onChange={(e) => set('usuario', e.target.value)}
                style={SS.input}
              />
            </div>
            <div>
              <label style={SS.label}>Contraseña</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  placeholder="Dejar en blanco"
                  style={{ ...SS.input, paddingRight: '38px' }}
                />
                <button
                  onClick={() => setShowPass((v) => !v)}
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '4px',
                    display: 'flex',
                  }}
                >
                  {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={SS.label}>Frecuencia</label>
            <select
              value={form.frecuencia}
              onChange={(e) => set('frecuencia', parseInt(e.target.value, 10))}
              style={{ ...SS.input, cursor: 'pointer' }}
            >
              {FRECUENCIAS.map((f) => (
                <option key={f.v} value={f.v}>
                  {f.l}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <div
              style={{
                fontSize: '12px',
                color: '#dc2626',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '10px',
                padding: '10px 14px',
                marginBottom: '14px',
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={onClose} style={SS.btnOutline}>
              Cancelar
            </button>
            <button
              onClick={guardar}
              disabled={guardando}
              style={{ ...SS.btnPrimary, opacity: guardando ? 0.6 : 1 }}
            >
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
